import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { parseUtcDate } from '../utils/dateFormat';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    admin_first_name: 'Admin',
    admin_last_name: '',
    admin_email: 'admin@tiataxi.al',
    company_name: 'TIATaxi Albania',
    theme: 'light',
    primary_color: '#dc2313',
    accent_color: '#fabc0c',
    language: 'sq',
    currency: 'EUR',
    secondary_currency: 'LEK',
    exchange_rate: 100,
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    timezone: 'Europe/Tirane'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
    
    // Listen for settings updates from SettingsPage
    const handleSettingsUpdate = () => fetchSettings();
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const fetchSettings = async () => {
    try {
      // Only fetch if user is logged in (has token)
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await api.get('/settings');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const refreshSettings = async () => {
    await fetchSettings();
  };

  // Helper to pad numbers with leading zero
  const pad = (num) => String(num).padStart(2, '0');

  // Get date parts in the correct timezone
  const getDateParts = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = parseUtcDate(dateStr);
      if (!date || isNaN(date.getTime())) return null;
      
      // Convert to the selected timezone
      const options = { timeZone: settings.timezone || 'Europe/Tirane' };
      const formatter = new Intl.DateTimeFormat('en-US', {
        ...options,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      const parts = formatter.formatToParts(date);
      const getPart = (type) => parts.find(p => p.type === type)?.value || '00';
      
      return {
        day: getPart('day'),
        month: getPart('month'),
        year: getPart('year'),
        hour: getPart('hour'),
        minute: getPart('minute')
      };
    } catch (e) {
      return null;
    }
  };

  // Format date according to settings
  const formatDate = (dateStr) => {
    const parts = getDateParts(dateStr);
    if (!parts) return '-';
    
    const { day, month, year } = parts;
    const format = settings.date_format || 'DD/MM/YYYY';
    
    switch (format) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'DD.MM.YYYY':
        return `${day}.${month}.${year}`;
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'MM-DD-YYYY':
        return `${month}-${day}-${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'YYYY/MM/DD':
        return `${year}/${month}/${day}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };

  // Format time according to settings
  const formatTime = (dateStr) => {
    const parts = getDateParts(dateStr);
    if (!parts) return '-';
    
    let { hour, minute } = parts;
    const is24h = settings.time_format === '24h';
    
    if (is24h) {
      return `${hour}:${minute}`;
    } else {
      // Convert to 12-hour format
      let h = parseInt(hour, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      if (h === 0) h = 12;
      return `${h}:${minute} ${ampm}`;
    }
  };

  // Format full date and time according to settings
  const formatDateTime = (dateStr) => {
    const dateFormatted = formatDate(dateStr);
    const timeFormatted = formatTime(dateStr);
    
    if (dateFormatted === '-' || timeFormatted === '-') return '-';
    
    return `${dateFormatted}, ${timeFormatted}`;
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = parseUtcDate(dateStr);
      if (!date || isNaN(date.getTime())) return '-';
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMins < 1) return 'Tani';
      if (diffMins < 60) return `${diffMins} min më parë`;
      if (diffHours < 24) return `${diffHours} orë më parë`;
      if (diffDays < 7) return `${diffDays} ditë më parë`;
      
      return formatDate(dateStr);
    } catch (e) {
      return '-';
    }
  };

  return (
    <SettingsContext.Provider value={{ 
      settings, 
      updateSettings, 
      refreshSettings, 
      loading,
      formatDate,
      formatTime,
      formatDateTime,
      formatRelativeTime
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
